import { artOfSkill, ELEMENT_INFO, SKILLS, type SkillDef } from '@nine/shared';
import { statusEmblem } from '../emblems';
import {
  bell,
  blade,
  bolt,
  claw,
  cloud,
  drop,
  eye,
  figure,
  fist,
  flames,
  groundRing,
  gust,
  inkStroke,
  lotus,
  moon,
  needle,
  palm,
  sabre,
  shield,
  slash,
  snowflake,
  speed,
  splats,
  sprite,
  star,
  swirl,
  sword,
  taiji,
  target,
  waves,
  brush,
  crack,
  fan,
  heart,
} from '../kit';
import { C, type Hex, mix, type Pix } from '../pix';
import { defineIconKind, type IconArt, type Marker, type Pal } from '../store';

/**
 * Moves (กระบวนท่า). The tile is the art's element; the kind mark (攻 破 守 療 怒) comes from the
 * move's kind. Moves without a hand-made picture get one composed from what they do: the weapon
 * and its effect, the shape it strikes in (a line, a ring round the caster, a circle on the
 * ground, a dash, a volley, a whirl, a chain of warps), how many times, and the status it leaves.
 */

const KIND_MARK: Readonly<Record<SkillDef['kind'], Marker>> = {
  attack: 'attack',
  break: 'break',
  power: 'power',
  heal: 'heal',
  buff: 'buff',
};

/** Monsters' moves have no art: a rust tile. */
const MONSTER_TINT = '#c8603a';

export const skillTint = (s: SkillDef): Hex => {
  const art = artOfSkill(s.id);
  return art ? ELEMENT_INFO[art.element].color : MONSTER_TINT;
};

const SABRE_FX = new Set(['sabre', 'chain', 'blood', 'bloodstorm']);
const bloody = (s: SkillDef) => s.fx === 'blood' || s.fx === 'bloodstorm';

/** The weapon a move is made with, as the generator draws it. */
function weaponOf(s: SkillDef): 'sword' | 'sabre' | 'hand' | 'beast' {
  const art = artOfSkill(s.id);
  if (!art) return 'beast';
  if (art.weapon === 'unarmed') return 'hand';
  return SABRE_FX.has(s.fx) || art.id.includes('sabre') ? 'sabre' : 'sword';
}

/** The weapon held point-up-right across the tile. */
function weapon(p: Pix, s: SkillDef, P: Pal, x0 = 6, y0 = 26, x1 = 26, y1 = 6): void {
  const w = weaponOf(s);
  const steel = bloody(s) ? mix(C.blood, C.white, 0.35) : C.steel;
  if (w === 'sabre') sabre(p, x0, y0, x1, y1, { blade: steel, hilt: P.c });
  else if (w === 'sword') sword(p, x0, y0, x1, y1, { hilt: P.c, blade: steel });
  else fist(p, 10, 10, C.skin);
}

/** The main picture of a strike, by its effect. */
function strike(p: Pix, s: SkillDef, P: Pal): void {
  const w = weaponOf(s);
  switch (s.fx) {
    case 'bolt':
      sword(p, 4, 28, 22, 10, { hilt: P.c });
      bolt(
        p,
        [
          [24, 2],
          [17, 11],
          [23, 12],
          [14, 24],
        ],
        P.l,
      );
      return;
    case 'pierce':
      speed(
        p,
        12,
        [
          [9, 7],
          [16, 9],
          [22, 6],
        ],
        P.l,
      );
      if (w === 'hand') {
        sprite(
          p,
          8,
          12,
          [
            '....ssssssssss..',
            '..ssssssssssssss',
            'ssssssssss......',
            'ssssssssss......',
            '.ssssssss.......',
          ],
          { s: C.skin },
        );
        star(p, 27, 13, 3, P.hi);
      } else {
        sword(p, 4, 16, 29, 16, { hilt: P.c, guard: 4 });
      }
      return;
    case 'nine':
    case 'needles':
      volley(p, s, P);
      return;
    case 'palm':
    case 'vajra':
      palms(p, s, P);
      return;
    case 'grab':
      claw(p, 9, 7, C.skin, P.l);
      slash(p, 16, 16, 12, 0.3, 2.6, P.c, 2);
      return;
    case 'quake':
      fist(p, 10, 4, w === 'hand' ? C.skin : C.steelMid);
      crack(
        p,
        [
          [16, 18],
          [13, 22],
          [15, 24],
          [11, 28],
        ],
        P.l,
      );
      crack(
        p,
        [
          [16, 18],
          [20, 22],
          [19, 25],
          [24, 28],
        ],
        P.l,
      );
      p.rect(4, 27, 24, 2, P.d);
      return;
    case 'bell':
      bell(p, 13, 9, 13, C.gold, { band: C.goldDark });
      waves(p, 19, 16, 7, 3, P.l, 0.9, 3);
      return;
    case 'shadow':
      if (w === 'sword') {
        moon(p, 10, 10, 7, '#f0e0ff');
        weapon(p, s, P, 6, 27, 27, 6);
      } else if (artOfSkill(s.id)?.id === 'chorus') {
        inkStroke(
          p,
          [
            [4, 24],
            [10, 14],
            [17, 18],
            [23, 8],
            [28, 11],
          ],
          4,
          '#ff9ad8',
        );
        inkStroke(
          p,
          [
            [4, 12],
            [11, 21],
            [18, 13],
            [26, 22],
          ],
          3,
          C.violet,
        );
      } else {
        claw(p, 8, 6, mix(C.violet, C.ink, 0.25), C.violet);
        slash(p, 18, 15, 11, -0.5, 2.0, C.violet, 2);
      }
      return;
    case 'brush':
      inkStroke(
        p,
        [
          [5, 22],
          [12, 18],
          [20, 19],
          [27, 12],
        ],
        5,
        C.ink,
      );
      p.path(
        [
          [6, 20],
          [12, 16],
          [20, 17],
          [26, 11],
        ],
        P.l,
      );
      brush(p, 26, 3, 15, 15);
      return;
    case 'ink':
      splats(
        p,
        [
          [16, 17, 6],
          [8, 12, 2.5],
          [24, 10, 2],
          [25, 23, 3],
          [9, 25, 2],
        ],
        C.ink,
      );
      p.ring(16, 17, 6, 6, P.l, -2.4, -0.3);
      for (const [x, y] of [
        [8, 12],
        [24, 10],
        [25, 23],
      ] as const)
        p.px(x, y - 1, P.l);
      return;
    case 'seal':
      sprite(
        p,
        4,
        14,
        ['......sssssssss.', '..sssssssssss...', 'sssssssss.......', 'sssssssss.......', '.ssssss.........'],
        { s: C.skin },
      );
      target(p, 24, 14, 4, P.l);
      return;
    case 'taiji':
      taiji(p, 16, 16, 9);
      p.ring(16, 16, 12, 12, P.l, -2.2, 0.4);
      return;
    case 'wind':
      sword(p, 6, 26, 26, 6, { hilt: P.c });
      gust(p, 4, 10, 10, P.l);
      gust(p, 14, 26, 10, P.l, false);
      return;
    case 'snow':
    case 'snowdash':
    case 'icebreak':
      weapon(p, s, P);
      snowflake(p, 9, 9, 5, C.ice);
      return;
    case 'blizzard':
      for (let k = 0; k < 6; k++)
        p.line(6 + k * 4, 3 + (k % 3) * 2, 8 + k * 4, 14 + (k % 2) * 3, k % 2 ? C.white : C.ice);
      snowflake(p, 16, 12, 5, C.white);
      return;
    case 'bite':
      sprite(
        p,
        6,
        6,
        [
          'ssssssssssssssssssss',
          'sssssssssssssssssss.',
          '.w.w..w.w..w..w.w...',
          '.w.w..w....w....w...',
          '....................',
          '...w...w....w..w....',
          '..w.w..w..w.w..w.w..',
          'sssssssssssssssssss.',
          'ssssssssssssssssss..',
        ],
        { s: mix(P.c, C.ink, 0.2), w: C.paper },
      );
      return;
    case 'slash':
      for (let k = 0; k < 3; k++) p.thick(9 + k * 6, 5, 3 + k * 6, 27, k === 1 ? C.white : P.l, 2);
      return;
    case 'crush':
      sprite(
        p,
        4,
        4,
        ['..dddddd....', '.dsssssssd..', 'dssssssssssd', 'dssssssssssd', '.dsssssssd..', '..dddddd....'],
        { s: '#8a6040', d: C.woodDark },
      );
      p.thick(12, 12, 24, 26, C.wood, 2);
      crack(
        p,
        [
          [6, 20],
          [9, 24],
          [7, 27],
        ],
        P.l,
      );
      return;
    case 'cleave':
      blade(p, 8, 26, 26, 4, { w: 6, col: C.steelMid, edge: C.white });
      slash(p, 14, 18, 12, -2.5, 0.2, P.l, 3);
      return;
    case 'ring':
      groundRing(p, 16, 20, 12, 6, P.l);
      groundRing(p, 16, 20, 7, 3.5, P.c);
      for (let k = 0; k < 5; k++) p.line(6 + k * 5, 3, 6 + k * 5, 12, k % 2 ? C.white : P.l);
      return;
    case 'whirl':
      swirl(p, 16, 16, 13, P.l, { core: C.white });
      if (w === 'sabre' || w === 'sword') weapon(p, s, P, 9, 23, 25, 7);
      return;
    default:
      if (w === 'hand') {
        claw(p, 7, 4, C.skin, C.white);
        for (let k = 0; k < 3; k++) p.thick(9 + k * 5, 16, 12 + k * 5, 28, C.blood, 2);
        return;
      }
      slash(p, 16, 16, 12, -2.4, 0.6, P.l, 3);
      weapon(p, s, P);
  }
}

/** Projectiles: as many as the move throws (up to eight), in a fan, or as rain when there are many. */
function volley(p: Pix, s: SkillDef, P: Pal): void {
  const art = artOfSkill(s.id)?.id;
  const n = Math.min(s.volley ?? 5, 8);
  const shot = (x0: number, y0: number, x1: number, y1: number, k: number) => {
    if (s.fx === 'nine') blade(p, x0, y0, x1, y1, { w: 2, col: k % 2 ? P.l : C.steel });
    else if (art === 'pulse-seal') {
      p.line(x0, y0, x1, y1, k % 2 ? C.white : P.l);
      p.ell(x1, y1, 1.2, 1.2, C.white);
    } else if (art === 'hidden-arts' && k % 2 === 0) {
      blade(p, x0, y0, x1, y1, { w: 2.4, col: C.steel });
      p.px(x0, y0, C.seal);
    } else needle(p, x0, y0, x1, y1, k % 2 ? C.steel : P.l);
  };
  if (n >= 8) {
    // A rain of them, falling slantwise.
    for (let k = 0; k < n; k++) {
      const x = 5 + ((k * 7) % 22);
      const y = 3 + ((k * 5) % 9);
      shot(x + 3, y, x - 1, y + 9 + (k % 3) * 2, k);
    }
    p.rect(4, 27, 24, 1, P.l);
  } else {
    if (art === 'pulse-seal') sprite(p, 12, 22, ['..ss..', '.ssss.', 'ssssss', 'ssssss'], { s: C.skin });
    else p.ell(16, 25, 3, 2, P.l);
    for (let k = 0; k < n; k++) {
      const a = -Math.PI / 2 - 0.95 + (1.9 * k) / Math.max(1, n - 1);
      shot(16 + Math.cos(a) * 5, 23 + Math.sin(a) * 5, 16 + Math.cos(a) * 14, 23 + Math.sin(a) * 18, k);
    }
  }
}

/** Palms: one flung far, one pressed onto a ring, several in a flurry, or one pushed out. */
function palms(p: Pix, s: SkillDef, P: Pal): void {
  const col = s.fx === 'vajra' ? '#ffcf5a' : C.skin;
  const lines = s.fx === 'vajra' ? '#c8862a' : C.skinDark;
  const hits = s.hits ?? 1;
  if (hits > 1) {
    for (let k = 2; k >= 0; k--)
      palm(p, 3 + k * 6, 11 - k * 3, k ? mix(col, C.ink, 0.15 * k) : col, { lines });
  } else if (s.aoe) {
    groundRing(p, 16, 24, 13, 5, P.l);
    palm(p, 9, 4, col, { lines, flipY: true });
  } else if (s.range >= 3.5 || s.dash) {
    speed(
      p,
      13,
      [
        [8, 8],
        [14, 10],
        [20, 7],
        [25, 9],
      ],
      P.l,
    );
    palm(p, 14, 9, col, { lines });
  } else {
    waves(p, 9, 16, 12, 3, P.l, 0.8, 3);
    palm(p, 5, 9, col, { lines });
  }
}

/** The picture of a buff or heal, by the status it gives (or its effect). */
function boon(p: Pix, s: SkillDef, P: Pal): void {
  const st = s.self?.status;
  if (s.kind === 'heal') {
    if (s.fx === 'taiji') taiji(p, 17, 15, 8);
    else if (s.fx === 'breath') {
      swirl(p, 17, 15, 10, P.l, { arms: 2, turn: 3.2 });
      cloud(p, 9, 24, 16, C.paper);
    } else if (bloody(s)) heart(p, 17, 15, 7, C.blood);
    else {
      if (weaponOf(s) === 'sword') sword(p, 17, 26, 17, 3, { hilt: P.c });
      else figure(p, 14, 8, P.l, 'sit');
    }
    lotus(p, 17, 27, 20, C.paper, P.l);
    return;
  }
  switch (st) {
    case 'iron-bell':
      bell(p, 17, 7, 16, C.gold, { band: C.goldDark });
      return;
    case 'haste':
      speed(
        p,
        14,
        [
          [10, 8],
          [16, 10],
          [22, 7],
        ],
        P.l,
      );
      figure(p, 15, 10, P.hi, 'run', { head: C.skin });
      figure(p, 15, 10, P.hi, 'run', { head: C.skin });
      return;
    case 'ward':
      if (s.fx === 'aura' && artOfSkill(s.id)?.id === 'ink-slate') fan(p, 17, 25, 14, C.paper, C.wood);
      else {
        shield(p, 17, 6, 16, 20, P.c, P.l);
        swirl(p, 17, 14, 6, P.hi, { arms: 2, turn: 3 });
      }
      return;
    case 'focus':
      eye(p, 17, 16, 18, P.c);
      star(p, 25, 7, 3, P.hi);
      return;
    case 'war-cry':
      weapon(p, s, P, 17, 28, 17, 4);
      for (let k = 0; k < 5; k++) {
        const a = -Math.PI / 2 + (k - 2) * 0.55;
        p.line(
          17 + Math.cos(a) * 9,
          9 + Math.sin(a) * 6,
          17 + Math.cos(a) * 13,
          9 + Math.sin(a) * 9,
          C.fireLight,
        );
      }
      return;
    case 'blood-frenzy':
      flames(p, 7, 28, 20, 18, C.blood, C.bloodLight);
      eye(p, 17, 17, 12, C.blood, C.fireLight);
      return;
    case 'trance':
      figure(p, 13, 9, P.l, 'leap', { head: C.skin });
      swirl(p, 17, 16, 12, '#ff9ad8', { arms: 2, turn: 2.4 });
      return;
    case 'snow-pride':
      sword(p, 17, 28, 17, 4, { hilt: P.c });
      snowflake(p, 17, 8, 5, C.white);
      return;
    default:
      figure(p, 14, 10, P.hi, 'stand', { head: C.skin });
      for (let k = 0; k < 5; k++)
        for (let y = 26 - (k % 2) * 3; y > 6 + (k % 3) * 3; y -= 2) p.px(7 + k * 5 + ((y >> 2) % 2), y, P.l);
  }
}

/** The shape a move strikes in, laid over its picture. */
function shapeOf(p: Pix, s: SkillDef, P: Pal): void {
  if (s.kind === 'heal' || s.kind === 'buff') return;
  if (s.blink) {
    for (let k = 0; k < 3; k++) figure(p, 3 + k * 6, 12, k === 2 ? P.hi : P.d, 'run');
  } else if (s.spin && s.fx !== 'whirl') swirl(p, 16, 16, 13, P.l);
  if (s.ground && s.fx !== 'ring' && s.fx !== 'blizzard') groundRing(p, 16, 25, 12, 4, P.l);
  else if (s.ground) groundRing(p, 16, 24, 12, 5, P.l);
  else if (s.aoe && !s.blink && !s.spin && s.fx !== 'whirl') p.ring(16, 16, 14, 14, P.l, -2.9, -0.2);
  if (s.dash === 'target') target(p, 25, 7, 3, P.hi);
  else if (s.dash === 'forward')
    speed(
      p,
      9,
      [
        [6, 6],
        [27, 7],
      ],
      P.hi,
    );
  const hits = s.hits ?? 1;
  if (hits > 1) for (let k = 0; k < Math.min(4, hits); k++) p.line(19 + k * 3, 3, 21 + k * 3, 6, C.white);
}

/** A move's generated picture. */
export function drawGeneratedSkill(p: Pix, s: SkillDef, P: Pal): void {
  if (s.kind === 'heal' || s.kind === 'buff') boon(p, s, P);
  else strike(p, s, P);
  shapeOf(p, s, P);
  const st = s.applies?.status ?? s.onGuardBreak?.status ?? (s.lifesteal ? 'lifesteal' : undefined);
  if (st && s.kind !== 'buff' && s.kind !== 'heal') statusEmblem(p, st, 2, 22);
  if (s.kind === 'break' && bloody(s)) drop(p, 25, 8, 2, C.blood);
}

export const generatedSkill = (s: SkillDef): IconArt => ({ draw: (p, c) => drawGeneratedSkill(p, s, c.p) });

defineIconKind('skill', {
  label: 'กระบวนท่า',
  ids: () => Object.keys(SKILLS),
  name: (id) => SKILLS[id]?.name ?? id,
  tint: (id) => (SKILLS[id] ? skillTint(SKILLS[id]) : MONSTER_TINT),
  plate: 'square',
  marker: (id) => KIND_MARK[SKILLS[id]?.kind ?? 'attack'],
  fallback: (id) => {
    const s = SKILLS[id];
    if (!s) return { draw: (p) => star(p, 16, 16, 6, C.paper) };
    return generatedSkill(s);
  },
});

export type { Hex };
